import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, fetchMe, startTrial, type MeResponse } from '@/lib/api';
import { haptic, notify } from '@/lib/telegram';

export interface TrialActivation {
  busy: boolean;
  created: boolean;
  error: string | null;
  start: () => Promise<void>;
}

function isReady(me: MeResponse): boolean {
  return (me.subscription.state === 'trial' || me.subscription.state === 'active')
    && Boolean(me.subscription.url.trim());
}

function pause(signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve();
    const done = () => {
      window.clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    };
    const timer = window.setTimeout(done, 1500);
    signal.addEventListener('abort', done, { once: true });
  });
}

export function useTrialActivation(
  active: boolean,
  onReady: (me: MeResponse) => void,
  onRefresh: (me: MeResponse) => void,
): TrialActivation {
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const request = useRef<AbortController | null>(null);
  const trialCreated = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    if (!active) request.current?.abort();
  }, [active]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      request.current?.abort();
    };
  }, []);

  const start = useCallback(async () => {
    if (request.current) return;
    const controller = new AbortController();
    const { signal } = controller;
    request.current = controller;
    setBusy(true);
    setError(null);
    haptic('medium');
    let timeout: number | undefined;
    let timedOut = false;

    const finish = (me: MeResponse) => {
      trialCreated.current = false;
      setCreated(false);
      notify('success');
      onReady(me);
    };

    try {
      if (!trialCreated.current) {
        try {
          await startTrial();
        } catch (err) {
          if (signal.aborted) return;
          if (err instanceof ApiError && err.status === 409) {
            const fresh = await fetchMe(signal);
            if (signal.aborted) return;
            if (isReady(fresh)) finish(fresh);
            else {
              onRefresh(fresh);
              setError('пробный период уже использован — можно оформить подписку');
            }
            return;
          }
          throw err;
        }
        trialCreated.current = true;
        if (!mounted.current) return;
        setCreated(true);
      }
      if (signal.aborted) return;

      // Keep the current screen while provisioning: /me can temporarily report
      // "expired" because a pending trial is already part of the user's history.
      timeout = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, 30_000);
      while (!signal.aborted) {
        try {
          const fresh = await fetchMe(signal);
          if (signal.aborted) break;
          if (isReady(fresh)) {
            finish(fresh);
            return;
          }
        } catch {
          // A transient read failure must not create another trial.
        }
        await pause(signal);
      }
      if (timedOut && mounted.current) {
        setError('триал создан, подключение ещё готовится — нажми «проверить подключение»');
      }
    } catch {
      if (!signal.aborted && mounted.current) {
        notify('error');
        setError('не получилось активировать триал. попробуй ещё раз.');
      }
    } finally {
      window.clearTimeout(timeout);
      if (request.current === controller) request.current = null;
      if (mounted.current) setBusy(false);
    }
  }, [onReady, onRefresh]);

  return { busy, created, error, start };
}
